/* ============================================================
   Flow Wake-Up Quiz — Shared React primitives
   Exposes: Progress, BrandHeader, Eyebrow, OptionButton, CalibrationLoader
            CheckIcon, useKeyHotkeys
   ============================================================ */

(function () {
  var jsx = window.jsx;
  var useState = React.useState;
  var useEffect = React.useEffect;
  var useRef = React.useRef;
  var useCallback = React.useCallback;

  function haptic(method, options) {
    var h = window.FlowHaptics;
    if (h && typeof h[method] === 'function') h[method](options);
  }

  // ------------------------------------------------------------
  // Icons
  // ------------------------------------------------------------
  function CheckIcon(props) {
    return jsx('svg', {
      viewBox: '0 0 20 20',
      fill: 'none',
      'aria-hidden': true,
      width: props.size || 12,
      height: props.size || 12,
      children: jsx('path', {
        d: 'M16.5 5.5L8.25 13.75L4 9.5',
        stroke: 'currentColor',
        strokeWidth: 2.4,
        strokeLinecap: 'round',
        strokeLinejoin: 'round'
      })
    });
  }
  window.CheckIcon = CheckIcon;

  // ------------------------------------------------------------
  // Progress bar — eased perceived progress so early steps don't
  // read as 1/17 (psychologically punishing). Same easing curve as
  // the Brody quiz but tuned for ~15 countable questions.
  // ------------------------------------------------------------
  function easedProgress(currentIdx, totalCountable) {
    if (totalCountable <= 1) return 1;
    var bounded = Math.max(0, Math.min(currentIdx, totalCountable));
    var raw = bounded / totalCountable;
    var decay = 2.0; // gentler than Brody's 2.4 because we have more questions
    var eased = (1 - Math.exp(-decay * raw)) / (1 - Math.exp(-decay));
    return 0.06 + 0.94 * eased;
  }

  function Progress(props) {
    var percent = Math.round(easedProgress(props.questionIdx, props.total) * 100);
    return jsx('div', {
      className: 'progress',
      role: 'progressbar',
      'aria-label': 'Quiz progress',
      'aria-valuemin': 0,
      'aria-valuemax': 100,
      'aria-valuenow': percent,
      children: jsx('div', {
        className: 'progress__bar',
        style: { width: percent + '%' }
      })
    });
  }
  window.Progress = Progress;

  // ------------------------------------------------------------
  // Brand header (top of every screen except the gate/reveal,
  // which compose their own layouts).
  // ------------------------------------------------------------
  function BrandHeader(props) {
    var QA = window.QA || {};
    // Render order: brand → back button → progress
    //   - On MOBILE the back button is `position: absolute` top-left (the
    //     brand is centered, so the absolute back button sits in empty
    //     space on the left)
    //   - On DESKTOP the back button becomes a normal-flow block, centered
    //     horizontally between brand and progress (matches Figma — back
    //     arrow sits inside the content column, not over the logo)
    return jsx('div', {
      className: 'shell__top',
      children: [
        jsx('div', {
          className: 'shell__brand',
          children: [
            jsx('img', {
              src: QA['logo-flow-symbol.svg'] || '../assets/logo-flow-symbol.svg',
              alt: '',
              'aria-hidden': true,
              className: 'shell__brand-symbol',
              width: 22,
              height: 22
            }),
            jsx('img', {
              src: QA['logo-flow-text.svg'] || '../assets/logo-flow-text.svg',
              alt: 'Flow',
              className: 'shell__brand-logo',
              width: 59,
              height: 20
            })
          ]
        }),
        props.canGoBack ? jsx(BackButton, { onClick: props.onBack }) : null,
        props.showProgress ? jsx(Progress, {
          questionIdx: props.questionIdx,
          total: props.total
        }) : null
      ]
    });
  }
  window.BrandHeader = BrandHeader;

  // ------------------------------------------------------------
  // BackButton — circular dark-gray button with white left-arrow.
  // Matches the Figma's back navigation pattern (visible on the
  // top-left of question / interstitial screens).
  // ------------------------------------------------------------
  function BackButton(props) {
    function handleClick(event) {
      haptic('back');
      props.onClick && props.onClick(event);
    }

    return jsx('button', {
      type: 'button',
      className: 'back-btn',
      onClick: handleClick,
      'aria-label': 'Go back',
      children: jsx('svg', {
        viewBox: '0 0 20 20',
        width: 18, height: 18, fill: 'none', 'aria-hidden': true,
        children: jsx('path', {
          d: 'M12.5 15L7.5 10L12.5 5',
          stroke: 'currentColor',
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round'
        })
      })
    });
  }
  window.BackButton = BackButton;

  // ------------------------------------------------------------
  // Eyebrow (small uppercase label above question titles)
  // ------------------------------------------------------------
  function Eyebrow(props) {
    if (!props.children) return null;
    return jsx('div', { className: 'eyebrow', children: props.children });
  }
  window.Eyebrow = Eyebrow;

  // Option button — matches Figma question template (11821:14470).
  // 72px tall pill, rgba(255,255,255,0.1) bg, 20px radius, centered
  // 21px Inter Display Medium label. No number badge, no check icon.
  function OptionButton(props) {
    var ref = useRef(null);
    var optionIndex = props.index || 0;
    var style = Object.assign({}, props.style || {}, {
      '--option-index': optionIndex,
      '--option-delay': (90 + optionIndex * 58) + 'ms'
    });

    useEffect(function () {
      if (props.autoFocus && ref.current) {
        ref.current.focus({ preventScroll: true });
      }
    }, [props.autoFocus]);

    function handleClick(event) {
      props.onClick && props.onClick(event);
    }

    return jsx('button', {
      type: 'button',
      ref: ref,
      className: 'option' + (props.selected ? ' is-selected' : ''),
      style: style,
      'aria-pressed': !!props.selected,
      onClick: handleClick,
      children: jsx('span', {
        className: 'option__body',
        children: [
          jsx('span', { className: 'option__label', children: props.label }),
          props.detail ? jsx('span', { className: 'option__detail', children: props.detail }) : null
        ]
      })
    });
  }
  window.OptionButton = OptionButton;

  // ------------------------------------------------------------
  // useCountUp — animate a number from `from` to `to` over `duration`ms
  // with cubic ease-out. Honors prefers-reduced-motion (jumps to `to`
  // immediately). `delay` defers the start without holding the value
  // at `from` longer than necessary.
  // ------------------------------------------------------------
  function useCountUp(to, opts) {
    var options = opts || {};
    var from = options.from || 0;
    var duration = options.duration || 800;
    var delay = options.delay || 0;
    var decimals = options.decimals || 0;
    // Use 10^decimals as the rounding factor so 3.5 stays 3.5 (not 4).
    var factor = Math.pow(10, decimals);

    var ref = useState(from);
    var value = ref[0];
    var setValue = ref[1];
    var milestoneRef = useRef({ halfway: false, done: false });

    useEffect(function () {
      milestoneRef.current = { halfway: false, done: false };
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) { setValue(to); haptic('success', { key: 'countup-reduced-' + to, minInterval: 500 }); return; }
      var raf = 0;
      var startAt = performance.now() + delay;
      function tick(now) {
        if (now < startAt) { raf = requestAnimationFrame(tick); return; }
        var t = Math.min((now - startAt) / duration, 1);
        if (t >= 0.5 && !milestoneRef.current.halfway) {
          milestoneRef.current.halfway = true;
          haptic('milestone', { key: 'countup-half-' + to, minInterval: 220 });
        }
        var eased = 1 - Math.pow(1 - t, 3);
        var raw = from + (to - from) * eased;
        setValue(Math.round(raw * factor) / factor);
        if (t < 1) {
          raf = requestAnimationFrame(tick);
        } else if (!milestoneRef.current.done) {
          milestoneRef.current.done = true;
          haptic('success', { key: 'countup-done-' + to, minInterval: 320 });
        }
      }
      raf = requestAnimationFrame(tick);
      return function () { cancelAnimationFrame(raf); };
    }, [to, from, duration, delay, factor]);

    return value;
  }
  window.useCountUp = useCountUp;

  // ------------------------------------------------------------
  // Keyboard hotkey hook — 1..9 selects nth option.
  // The Brody quiz had numbered badges that *implied* hotkeys but
  // didn't wire them. We wire them.
  // ------------------------------------------------------------
  function useKeyHotkeys(count, onPick) {
    useEffect(function () {
      function handler(e) {
        // Don't hijack typing in form fields.
        var tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (e.metaKey || e.ctrlKey || e.altKey) return;

        var n = parseInt(e.key, 10);
        if (Number.isFinite(n) && n >= 1 && n <= count) {
          e.preventDefault();
          onPick(n - 1);
        }
      }
      window.addEventListener('keydown', handler);
      return function () { window.removeEventListener('keydown', handler); };
    }, [count, onPick]);
  }
  window.useKeyHotkeys = useKeyHotkeys;

  // ------------------------------------------------------------
  // Calibration loader — staged "we're matching you" lines.
  // Each line goes pending → active → done over `perLineMs` ms.
  // When the last line completes, `onComplete` fires.
  // ------------------------------------------------------------
  function CalibrationLoader(props) {
    var lines = props.lines || [];
    var perLineMs = props.perLineMs || 900;
    var stateRef = useState(0);
    var idx = stateRef[0];
    var setIdx = stateRef[1];

    useEffect(function () {
      if (idx >= lines.length) {
        haptic('success', { key: 'calibration-complete', minInterval: 500 });
        var done = setTimeout(function () { props.onComplete && props.onComplete(); }, 380);
        return function () { clearTimeout(done); };
      }
      if (idx > 0) haptic('selection', { key: 'calibration-step', minInterval: 180 });
      var t = setTimeout(function () { setIdx(idx + 1); }, perLineMs);
      return function () { clearTimeout(t); };
    }, [idx]);

    return jsx('div', {
      className: 'calibration',
      children: [
        jsx('h2', { className: 'calibration__title', children: props.title || 'Matching you with your wake-up plan…' }),
        jsx('ul', {
          className: 'calibration__lines',
          children: lines.map(function (text, i) {
            var status = i < idx ? 'is-done' : (i === idx ? 'is-active' : '');
            return jsx('li', {
              key: String(i),
              className: 'cal-line ' + status,
              children: [
                jsx('span', {
                  className: 'cal-line__bullet',
                  children: i < idx
                    ? jsx(CheckIcon, { size: 12 })
                    : (i === idx ? jsx('span', { className: 'cal-spinner' }) : null)
                }),
                jsx('span', { children: text })
              ]
            });
          })
        })
      ]
    });
  }
  window.CalibrationLoader = CalibrationLoader;
})();
